import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import { CheckCircle, ErrorOutline } from '@mui/icons-material';
import { auth } from '../config/firebase';
import { applyActionCode } from 'firebase/auth';

type Status = 'verifying' | 'success' | 'error';

/**
 * Public landing page for the Firebase email-verification link
 * (`/verify-email?oobCode=…&email=…`). Applies the action code, refreshes the
 * local auth state, then routes the user into the app (or to login if they
 * opened the link on a device where they aren't signed in).
 */
const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');
  const email = searchParams.get('email') || '';
  const [status, setStatus] = useState<Status>('verifying');
  const [error, setError] = useState<string>('');

  const verify = useCallback(async () => {
    if (!oobCode) {
      setStatus('error');
      setError('This verification link is invalid or incomplete. Please request a new one.');
      return;
    }
    try {
      await applyActionCode(auth, oobCode);
      // Refresh local auth state so the app/guard sees the verified flag without
      // a full re-login. Best-effort — the backend reads the live Firebase
      // record, so the next request passes regardless.
      try {
        await auth.currentUser?.reload();
        await auth.currentUser?.getIdToken(true);
      } catch {
        /* ignore: live record is the source of truth */
      }
      setStatus('success');
      setTimeout(() => {
        // Logged in (same browser as signup) → resume the onboarding wizard,
        // which persists the survey + owner profile/terms. Not logged in (link
        // opened elsewhere) → login.
        navigate(auth.currentUser ? '/onboarding' : '/login', { replace: true });
      }, 2500);
    } catch (e: any) {
      setStatus('error');
      if (e?.code === 'auth/expired-action-code') {
        setError('This verification link has expired. Please request a new one.');
      } else if (e?.code === 'auth/invalid-action-code') {
        setError(
          'This verification link is invalid or has already been used. If you have not verified yet, request a new one.',
        );
      } else {
        setError('We could not verify your email. Please try again or request a new link.');
      }
    }
  }, [oobCode, navigate]);

  useEffect(() => {
    verify();
  }, [verify]);

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          component="img"
          src="/StableTrack.png"
          alt="StableTrack"
          sx={{ height: 120, width: 120, mb: 3 }}
        />
        <Card sx={{ width: '100%', boxShadow: 3 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {status === 'verifying' && (
              <>
                <CircularProgress sx={{ color: '#C9772B', mb: 2 }} />
                <Typography variant="h6">Verifying your email…</Typography>
              </>
            )}
            {status === 'success' && (
              <>
                <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Email verified!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {email ? `${email} is confirmed. ` : ''}Taking you to StableTrack…
                </Typography>
              </>
            )}
            {status === 'error' && (
              <>
                <ErrorOutline sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Verification failed
                </Typography>
                <Alert severity="error" sx={{ my: 2, textAlign: 'left' }}>
                  {error}
                </Alert>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate('/verify-pending')}
                  sx={{ mt: 1, bgcolor: '#C9772B', '&:hover': { bgcolor: '#6B3410' } }}
                >
                  Get a new link
                </Button>
                <Button
                  variant="text"
                  onClick={() => navigate('/login')}
                  sx={{ mt: 1, color: '#C9772B', textTransform: 'none' }}
                >
                  Back to Login
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default VerifyEmail;
